import { isNull } from 'lodash';
import { SectionSeparator } from '@styles/layout';
import { TripTypes } from '@model/common';
import { Name } from '@model/iceberg';
import { PageTemplates, TemplateComponent, ComplexComponent, ComplexComponentTypes } from '@model/content';
import { ComponentHandles, ComponentTypes } from '@util/content';
import { Logging } from '@util/logging';

interface ComponentsContainerProps {
  components: Array<TemplateComponent>;
  componentsData?: Array<any>;
  path?: string;
  tripType?: TripTypes;
  type?: PageTemplates;
  destination?: Name;
  isWhiteLabel?: boolean;
}

const WHITE_LABEL_COMPONENTS_BLACKLIST = [
  'popularHotels',
  'specialOffers',
  'exploreDestination',
  'tourGroup',
  'testimonials',
  'testimonialsComponent',
  'yourRecentlyReviewed'
];

interface GetRenderedComponentProps {
  component: TemplateComponent;
  path?: string;
  tripType?: TripTypes;
  destination?: Name;
}
const getRenderedComponent = ({ component, ...pageProps }: GetRenderedComponentProps) => {
  const { handle, ...props } = component;
  const Component = ComponentTypes.get(handle);
  if (Component) {
    return <Component {...pageProps} {...props} />;
  } else {
    Logging.warn({ text: `No mapped component for ${handle}` });
    return undefined;
  }
};

const ComplexComponents = [ComponentHandles.TAB_GROUP, ComponentHandles.GRID_CONTAINER];

export const renderComponents = ({
  components,
  componentsData,
  path,
  tripType,
  type,
  destination,
  isWhiteLabel
}: ComponentsContainerProps) => {
  let hasCallToBook: boolean = !!components.find(({ handle }) => handle === ComponentHandles.CALL_TO_BOOK);
  let complexContainerIndex: number | null = null;
  let complexContainerProps: any = null;
  const mapComponent = (component: TemplateComponent, idx: number) => {
    const { handle, ...rest } = component;

    // some components never show on white label sites so we filter them here
    // replacing the first instance with a call to book component and ignoring the rest
    if (isWhiteLabel && WHITE_LABEL_COMPONENTS_BLACKLIST.includes(handle)) {
      if (hasCallToBook || tripType != TripTypes.HOLIDAYS) {
        return null;
      }
      hasCallToBook = true;
      const CallToBookComponent = ComponentTypes.get(ComponentHandles.CALL_TO_BOOK);
      return (
        <>
          <SectionSeparator />
          <CallToBookComponent path={path} tripType={tripType} destination={destination} />
        </>
      );
    }

    let data: any = null;
    if (ComplexComponents.includes(handle as ComponentHandles)) {
      const { type, ...props } = component as ComplexComponent;
      if (type === ComplexComponentTypes.OPEN) {
        complexContainerIndex = idx;
      } else if (!isNull(complexContainerIndex)) {
        complexContainerProps = props;
        const startIndex = complexContainerIndex + 1;
        complexContainerIndex = null;
        data = components.slice(startIndex, idx).map((component) => {
          const renderable = getRenderedComponent({ component, path, destination, tripType });
          return { ...component, renderable };
        });
      }
    }

    const MappedComponent = !isNull(complexContainerIndex) ? null : ComponentTypes.get(handle);

    if (!MappedComponent && isNull(complexContainerIndex)) {
      Logging.warn({ text: `No mapped component for ${handle}` });
    }

    return MappedComponent ? (
      <MappedComponent
        {...rest}
        {...complexContainerProps}
        data={componentsData && componentsData[idx] ? componentsData[idx] : data}
        path={path}
        tripType={tripType}
        pageType={type}
        destination={destination}
        key={`${handle}-${idx}`}
        className={handle}
      />
    ) : null;
  };
  return components.map(mapComponent);
};

export const ComponentsContainer = (props: ComponentsContainerProps) => {
  return (
    <section>
      {renderComponents(props)}
      <SectionSeparator size={50} />
    </section>
  );
};
